import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch } from './http';

export interface PlayerStatsResponse {
  side: 'away' | 'home' | null;
  current_game: {
    id: number | null;
    name: string | null;
    jersey: string | null;
    position: string | null;
    batting: Record<string, unknown> | null;
    pitching: Record<string, unknown> | null;
  };
  season_group: 'hitting' | 'pitching';
  season: { stats?: Array<{ splits?: Array<{ stat?: Record<string, unknown> }> }> } | null;
}

/**
 * Shared query options factory — used by `usePlayerStats` (the modal's
 * live read) and `usePrefetchPlayers` (the game view's warming hook).
 *
 * Cache-scope decision: the key omits the user UID because the backend's
 * `/api/games/{game_pk}/players/{player_id}` route returns the same JSON
 * for any authenticated viewer (player stats are not user-scoped, only
 * auth-gated). If we ever add a user-personalized field, bump the key to
 * include the UID.
 */
export function playerStatsQueryOptions(
  gamePk: number | undefined,
  playerId: number | null,
  getIdToken: () => Promise<string>,
) {
  return {
    queryKey: ['player-stats', gamePk, playerId] as const,
    queryFn: () =>
      apiFetch<PlayerStatsResponse>(`/api/games/${gamePk}/players/${playerId}`, { getIdToken }),
    // 5 minutes — prefetched player data isn't immediately re-fetched on click,
    // so the modal paints from cache with no flicker. Player stats don't change
    // mid-game beyond what the live stream already updates separately.
    staleTime: 5 * 60_000,
  };
}

export function usePlayerStats(gamePk: number | undefined, playerId: number | null) {
  const { getIdToken, user } = useAuth();
  return useQuery<PlayerStatsResponse>({
    ...playerStatsQueryOptions(gamePk, playerId, getIdToken),
    enabled: Boolean(user && gamePk && playerId),
  });
}
